import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import { BandLegend } from "../band/band-legend";
import type { PanelBand } from "../band/use-panel-band";
import { RULE_COLOR } from "../panel-tokens";
import type { PanelFocus } from "../use-panel-focus";
import { usePanelReadout } from "./readout-context";
import { formatReadoutTime } from "./readout-format";
import { ReadoutLegend } from "./readout-legend";
import { PILL, TimePill } from "./time-pill";

/** Everything under the plot: the divider with Show all and the time pill, the irrigation row, the legend. */
export function PanelReadout({
    getDisplayInfo,
    band,
    focus,
}: {
    getDisplayInfo: GetDisplayInfo;
    band: PanelBand | undefined;
    focus: PanelFocus;
}): JSX.Element {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "readout" });
    const { readout, nowMs } = usePanelReadout();
    const focusedStreamId = focus.focusedStream?.streamId;

    return (
        <div>
            <div className={"flex h-[22px] items-center gap-[6px]"}>
                {focusedStreamId !== undefined && (
                    <Button
                        variant={"outline"}
                        size={"xs"}
                        // The time pill's shape, so the row keeps its 22px; the sm: and before: rules beat the xs size's own.
                        className={cn(PILL, "before:rounded-[9px] sm:h-[18px] sm:text-[11px]")}
                        onClick={focus.clearFocus}
                    >
                        {t("show-all")}
                    </Button>
                )}
                {/* Pinned to a whole pixel: centred, a 1px rule in 22px paints two grey pixels at 1x. */}
                <span
                    aria-hidden
                    className={"mt-[11px] h-px flex-1 self-start"}
                    style={{ backgroundColor: RULE_COLOR }}
                />
                {readout.timeMs !== null && readout.mode !== "past" && (
                    <TimePill mode={readout.mode} time={formatReadoutTime(readout.mode, readout.timeMs, nowMs)} />
                )}
            </div>
            {band && <BandLegend scheduledMs={band.totals.scheduledMs} actualMs={band.totals.actualMs} />}
            <ReadoutLegend
                rows={readout.rows}
                getDisplayInfo={getDisplayInfo}
                focusedStreamId={focusedStreamId}
                onToggleFocus={focus.toggleFocus}
            />
        </div>
    );
}
